/**
 * 外部轨迹 eventCode 的 i18n 工具
 * 优先 central.parcels.timeline.eventCodes，回退复用 enum.orderEvent
 */
export const EXTERNAL_EVENT_CODE_I18N_PREFIX = 'central.parcels.timeline.eventCodes' as const;

/** 内部轨迹 i18n 前缀（复用） */
export const INTERNAL_ORDER_EVENT_I18N_PREFIX = 'enum.orderEvent' as const;

/** 获取 eventCode 的 i18n 键 */
export function getExternalEventCodeI18nKey(code: string): string {
    return `${EXTERNAL_EVENT_CODE_I18N_PREFIX}.${code}`;
}

/** 将 reasonCode 规范化为 i18n 键（空格转下划线，保留字母数字下划线） */
function normalizeReasonCodeForKey(reasonCode: string): string {
    return (reasonCode || '').trim().replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '') || '';
}

/** 获取 eventCode+reasonCode 组合的 i18n 键 */
export function getExternalEventCodeWithReasonI18nKey(eventCode: string, reasonCode: string | null | undefined): string {
    const code = (eventCode || '').trim();
    const reason = (reasonCode || '').trim();
    if (!code) return '';
    if (!reason) return getExternalEventCodeI18nKey(code);
    const normalizedReason = normalizeReasonCodeForKey(reason);
    return `${EXTERNAL_EVENT_CODE_I18N_PREFIX}.${code}_${normalizedReason}`;
}

/** 描述 i18n 键后缀 */
const DESCRIPTION_I18N_SUFFIX = '_desc';

/**
 * 当 reasonCode 为 null 时，根据 API description 关键词推断 reasonCode
 * 用于匹配已有的 eventCode_reasonCode_desc 国际化文案
 * 匹配顺序按优先级，先匹配到的生效
 */
const DELIVERY_FAILED_DESC_PATTERNS: Array<{ pattern: RegExp; reasonCode: string }> = [
    { pattern: /Unable to access|restricted entry|security requirements/i, reasonCode: 'Unable_to_access' },
    { pattern: /could not be located|Wrong address|address could not be located/i, reasonCode: 'Wrong_Address' },
    { pattern: /Business was closed|Business closed|business closed/i, reasonCode: 'Business_Closed' },
    { pattern: /refused to accept|Customer refused|customer refused/i, reasonCode: 'Customer_Refuse' },
    { pattern: /attempt unsuccessful|Delivery attempt unsuccessful/i, reasonCode: 'DeliveryAttempt' }
];

const PICKUP_FAILED_DESC_PATTERNS: Array<{ pattern: RegExp; reasonCode: string }> = [
    { pattern: /Wrong address|unable to locate pickup/i, reasonCode: 'Wrong_Address' },
    { pattern: /not ready for collection|Package not ready/i, reasonCode: 'Package_Not_Ready' },
    { pattern: /Warehouse closed|warehouse closed/i, reasonCode: 'Warehouse_Closed' },
    { pattern: /handed to another carrier|another carrier/i, reasonCode: 'Handed_To_Another_Carrier' },
    { pattern: /exceeds size|weight limitations|Oversized/i, reasonCode: 'Package_Oversized' },
    { pattern: /Invalid phone|invalid or disconnected|phone number/i, reasonCode: 'Invalid_phone_number' },
    { pattern: /Unable to reach contact|reach contact person/i, reasonCode: 'Cannot_reach_contact' },
    { pattern: /unavailable to hand over|Contact unavailable/i, reasonCode: 'Package_Not_Handed_Over' },
    { pattern: /delay in pickup|Pickup delayed|operational constraints/i, reasonCode: 'Pickup_Delayed' }
];

export function inferReasonCodeFromDescription(
    eventCode: string,
    description: string | null | undefined
): string | null {
    const desc = (description || '').trim();
    if (!desc) return null;
    const code = (eventCode || '').trim().toUpperCase();
    const patterns =
        code === 'DELIVERY_FAILED'
            ? DELIVERY_FAILED_DESC_PATTERNS
            : code === 'PICKUP_FAILED'
              ? PICKUP_FAILED_DESC_PATTERNS
              : [];
    for (const { pattern, reasonCode } of patterns) {
        if (pattern.test(desc)) return reasonCode;
    }
    return null;
}

/** 获取 eventCode+reasonCode 对应描述的 i18n 键 */
export function getExternalEventDescriptionI18nKey(eventCode: string, reasonCode: string | null | undefined): string {
    const combinedKey = getExternalEventCodeWithReasonI18nKey(eventCode, reasonCode);
    if (!combinedKey) return '';
    return `${combinedKey}${DESCRIPTION_I18N_SUFFIX}`;
}

/** 获取内部轨迹 i18n 键（用于回退复用） */
export function getInternalOrderEventI18nKey(code: string): string {
    return `${INTERNAL_ORDER_EVENT_I18N_PREFIX}.${code}`;
}

/** OrderEventData 实体类名前缀（断更明细 ETL 写入的轨迹名带此前缀） */
const ORDER_EVENT_DATA_PREFIX = 'OrderEventData';

/**
 * OrderEventData 实体类名（如 OrderEventDataDelivered）→ enum.orderEvent 短码（Delivered）。
 * 断更明细 last_tracking_event_name / no_update_start_event_name 等字段由 ETL 写入实体类名（带前缀），
 * 而 enum.orderEvent 键为短码，二者差一个 OrderEventData 前缀；无前缀时（如 dev seed 的 ArrivalScan）原样返回。
 */
export function stripOrderEventDataPrefix(name: string): string {
    return name.startsWith(ORDER_EVENT_DATA_PREFIX) ? name.slice(ORDER_EVENT_DATA_PREFIX.length) : name;
}

/**
 * 外部 eventCode 到内部 enum.orderEvent 键的显式映射
 * 用于 UPPER_SNAKE 与内部键名不一致、或渠道英文 description 作为 eventCode 时
 */
const EXTERNAL_TO_INTERNAL_KEY_MAP: Record<string, string> = {
    'RETURN_SHIPPED': 'ReturnScan',
    'HANDOVER': 'HandedOver',
    'TransportDepartureScan': 'TransportDepartureScan',
    'RETURN_ORIGIN_HUB_ARRIVED': 'ReturnArrivalScan',
    'RETURN_HUB_DISPATCHED': 'ReturnScan',
    'RETURN_DESTINATION_HUB_RECEIVED': 'ReturnArrivalScan',
    'RETURN_DESTINATION_HUB_DISPATCHED': 'ReturnScan',
    'Shipping Label Created': 'Created',
    'Shipment Received at Facility': 'PickupArrivalScan',
    'Arrived At Warehouse': 'WhsArrivalScan',
    'Transport departure': 'TransportDepartureScan',
    'Out for Delivery': 'OutForDelivery',
    'Delivered': 'Delivered',
    'In Transit': 'DispatchScan',
    'Return To Sender': 'ReturnScan',
    'Returned And Delivered': 'Returned',
    'Delivery failed': 'DeliveryFailed'
};

/** UPPER_SNAKE_CASE 转 PascalCase */
export function toPascalCase(snake: string): string {
    return snake
        .split('_')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join('');
}

/**
 * 将外部 eventCode 转为内部 enum.orderEvent 键（用于 i18n 回退）
 * 若无法映射则返回 null
 */
export function getInternalOrderEventKeyForExternalCode(externalCode: string): string | null {
    const trimmed = (externalCode || '').trim();
    if (!trimmed) return null;
    if (EXTERNAL_TO_INTERNAL_KEY_MAP[trimmed]) {
        return EXTERNAL_TO_INTERNAL_KEY_MAP[trimmed];
    }
    const pascal = toPascalCase(trimmed);
    return pascal || null;
}
